import { SalesActionsType } from 'api-hooks/general/model';
import { SaleModel } from 'api-hooks/sale/common';
import { AuthorizationRules } from 'common/constants';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDismiss from 'hooks/use-dismiss';
import useResolveApproval from 'hooks/use-resolve-approval';
import useVoidAction from 'hooks/use-void-action';
import { ProductSaleFormType } from 'legacy-modules/product/sales/components/form-type';
import { TourSaleFormType } from 'legacy-modules/tour/tour-sale/components/form-type';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useFlightOpenTicket from './use-flight-open-ticket';
import useHotelSaveVoucher from './use-hotel-save-voucher';
import useSalesAddToProject from './use-sales-add-to-project';
import useSalesCompleteAction from './use-sales-complete';
import useSalesDuplicate from './use-sales-duplicate';
import useSalesOtherExpense from './use-sales-other-expense';
import useSalesOtherIncome from './use-sales-other-income';
import useSalesRefund from './use-sales-refund';
import useSalesReschedule from './use-sales-reschedule';
import useSalesSaveInvoice from './use-sales-save-invoice';
import useSalesToOrder from './use-sales-to-order';
import { ActivitySaleFormType } from '../activity/components/form-type';
import { CruiseSaleFormType } from '../cruise/components/form-type';
import { HotelSaleFormType } from '../hotel/components/form-type';
import { TicketSaleFormType } from '../ticket-sale/components/form-type';
import { DocumentSaleFormType } from '../travel-document/components/form-type';

export interface UseSalesActionsProps {
  type: SalesActionsType;
  data: SaleModel;
  defaultValues:
    | HotelSaleFormType
    | DocumentSaleFormType
    | CruiseSaleFormType
    | TicketSaleFormType
    | ActivitySaleFormType
    | TourSaleFormType
    | ProductSaleFormType;
}

export default function useSalesActions(props: UseSalesActionsProps) {
  const { data, type, defaultValues } = props;
  const { t } = useTranslation();
  const { addToProjectPermission, onAddProject } = useSalesAddToProject(data);
  const onDismiss = useDismiss(type);

  const {
    incompletePermission,
    completePermission,
    onComplete,
    onRevertComplete,
    completeComponent,
    incompleteComponent,
  } = useSalesCompleteAction(type, data);
  const { can } = useAuthorization();

  const { requestApprovalButton } = useApprovalAction(type, data);

  const { duplicatePermission, onDuplicate, createSaleRouter } =
    useSalesDuplicate(type, data, defaultValues);

  const { onOtherExpense, otherExpensePermission } = useSalesOtherExpense(data);

  const { onOtherIncome, otherIncomePermission } = useSalesOtherIncome(data);

  const { onRefund, refundPermission } = useSalesRefund(type, data);

  const { onReschedule, reschedulePermission } = useSalesReschedule(type, data);

  const { onCreateOrder, orderPermission, createOrderRoute } = useSalesToOrder(
    type,
    data,
  );

  const { onResolve, resolverPermission, resolverButton } = useResolveApproval(
    type,
    data,
  );

  const { onSaveInvoice, saveInvoicePermission } = useSalesSaveInvoice(
    type,
    data,
  );

  const { onSaveHotelVoucher, saveVoucherPermission } = useHotelSaveVoucher(
    type,
    data,
  );

  const { onVoid, voidPermission } = useVoidAction(type, data);

  const { openTicketAction, openTicketPermission } = useFlightOpenTicket(
    data,
    type,
  );

  const unauthorizedNotification = React.useCallback(() => {
    notification.error({
      message: t('common:unauthorized_action'),
    });
  }, [t]);

  const actionComponentPermission = React.useMemo(() => {
    return {
      addToProjectPermission,
      incompletePermission,
      completePermission,
      duplicatePermission,
      otherExpensePermission,
      otherIncomePermission,
      refundPermission,
      reschedulePermission,
      orderPermission,
      resolverPermission,
      saveInvoicePermission,
      saveVoucherPermission,
      voidPermission,
      openTicketPermission,
    };
  }, [
    addToProjectPermission,
    completePermission,
    duplicatePermission,
    incompletePermission,
    openTicketPermission,
    orderPermission,
    otherExpensePermission,
    otherIncomePermission,
    refundPermission,
    reschedulePermission,
    resolverPermission,
    saveInvoicePermission,
    saveVoucherPermission,
    voidPermission,
  ]);

  const createAuthorization = React.useMemo(() => {
    switch (type) {
      case 'flight-sales':
        return AuthorizationRules.FlightSalesCreate;
      case 'hotel-sales':
        return AuthorizationRules.HotelSalesCreate;
      case 'cruise-sales':
        return AuthorizationRules.CruiseSalesCreate;
      case 'document-sales':
        return AuthorizationRules.DocumentSalesCreate;
      case 'activity-sales':
        return AuthorizationRules.ActivitySalesCreate;
      case 'product-sales':
        return AuthorizationRules.ProductSalesCreate;
      case 'tour-sales':
        return AuthorizationRules.TourSalesCreate;
    }
  }, [type]);

  const isAllowedCreate = can(createAuthorization);

  const rescheduleAction: PopoverMenuType = {
    label: t('common:reschedule'),
    onClick: onReschedule,
    isAllowed: actionComponentPermission.reschedulePermission,
    isError: false,
    key: `reschedule-${data.id}`,
  };

  const duplicateInvoiceAction: PopoverMenuType = {
    label: t('common:duplicate_invoice'),
    onClick: onDuplicate,
    isAllowed: actionComponentPermission.duplicatePermission && isAllowedCreate,
    isError: false,
    key: `duplicate-${data.id}`,
  };

  const createOrderAction: PopoverMenuType = {
    label: t('common:create_extra', { extra: t('common:order') }),
    onClick: onCreateOrder,
    isAllowed: actionComponentPermission.orderPermission,
    isError: false,
    key: `create-order-${data.id}`,
  };

  const refundAction: PopoverMenuType = {
    label: t('common:refund'),
    onClick: onRefund,
    isAllowed: actionComponentPermission.refundPermission,
    isError: false,
    key: `refund-${data.id}`,
  };

  const otherIncomeAction: PopoverMenuType = {
    label: t('common:other_incomes'),
    onClick: onOtherIncome,
    isAllowed: actionComponentPermission.otherIncomePermission,
    isError: false,
    key: `other-income-${data.id}`,
  };

  const otherExpenseAction: PopoverMenuType = {
    label: t('common:other_expenses'),
    onClick: onOtherExpense,
    isAllowed: actionComponentPermission.otherExpensePermission,
    isError: false,
    key: `other-expense-${data.id}`,
  };

  const addProjectAction: PopoverMenuType = {
    label: t('common:add_to_project'),
    onClick: onAddProject,
    isAllowed: actionComponentPermission.addToProjectPermission,
    isError: false,
    key: `add-project-${data.id}`,
  };

  const saveInvoiceAction: PopoverMenuType = {
    label: t('ticketSales:save_invoice_pdf'),
    onClick: onSaveInvoice,
    isAllowed: actionComponentPermission.saveInvoicePermission,
    isError: false,
    key: `save-invoice-${data.id}`,
  };

  const saveVoucherAction: PopoverMenuType = {
    label: t('hotel:save_voucher'),
    onClick: onSaveHotelVoucher,
    isAllowed: actionComponentPermission.saveVoucherPermission,
    isError: false,
    key: `save-voucher-${data.id}`,
  };

  const voidAction: PopoverMenuType = {
    label: t('common:void'),
    onClick: onVoid,
    isAllowed: actionComponentPermission.voidPermission,
    isError: true,
    key: `void-${data.id}`,
  };

  const completeAction: PopoverMenuType = {
    label: t('common:complete'),
    onClick: onComplete,
    isAllowed: actionComponentPermission.completePermission,
    isError: false,
    key: `complete-${data.id}`,
  };

  const incompleteAction: PopoverMenuType = {
    label: t('common:incomplete'),
    onClick: onRevertComplete,
    isAllowed: actionComponentPermission.incompletePermission,
    isError: true,
    key: `incomplete-${data.id}`,
  };

  const actions = [
    duplicateInvoiceAction,
    createOrderAction,
    otherIncomeAction,
    otherExpenseAction,
    refundAction,
    rescheduleAction,
    openTicketAction,
    addProjectAction,
    saveInvoiceAction,
    saveVoucherAction,
    voidAction,
  ].filter((action) => action.isAllowed);

  return {
    onOtherExpense,
    onOtherIncome,
    onAddProject,
    onRefund,
    onResolve,
    onSaveInvoice,
    onSaveHotelVoucher,
    onVoid,
    onDuplicate,
    onCreateOrder,
    onRevertComplete,
    onComplete,
    unauthorizedNotification,
    onDismiss,
    actions,
    actionComponentPermission,
    createOrderRoute,
    rescheduleAction,
    duplicateInvoiceAction,
    createOrderAction,
    otherIncomeAction,
    otherExpenseAction,
    addProjectAction,
    saveInvoiceAction,
    saveVoucherAction,
    voidAction,
    completeAction,
    incompleteAction,
    refundAction,
    completeComponent,
    incompleteComponent,
    createSaleRouter,
    createAuthorization,
    requestApprovalButton,
    resolverButton,
  };
}
